import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';
import { searchByName } from '../utils/searchText';

type Option = { id: string; name: string };

/**
 * Bloco #72: campo Paciente com busca. Digita parte do nome ou do código
 * (sem diferença de acento/maiúsculas), navega com as setas, Enter seleciona
 * e Esc fecha. Lista só os pacientes recebidos (o vínculo do #62 já vem filtrado).
 */
export const PatientSearchSelect: React.FC<{
  patients: Option[];
  value: string;
  onChange: (id: string) => void;
  required?: boolean;
  placeholder?: string;
}> = ({ patients, value, onChange, required, placeholder = 'Digite o nome ou o código do paciente...' }) => {
  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const selected = patients.find((p) => p.id === value) || null;
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);

  const results = useMemo(() => searchByName(patients, query), [patients, query]);

  useEffect(() => setActive(0), [query, open]);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent | TouchEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('touchstart', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('touchstart', close);
    };
  }, [open]);
  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const choose = (p: Option) => {
    onChange(p.id);
    setQuery('');
    setOpen(false);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActive((i) => Math.min(i + 1, Math.max(results.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      if (open && results[active]) {
        e.preventDefault();
        choose(results[active]);
      }
    } else if (e.key === 'Escape') {
      if (open) {
        e.preventDefault();
        e.stopPropagation();
        setOpen(false);
        setQuery('');
      }
    }
  };

  const shown = open ? query : selected?.name ?? query;

  return (
    <div ref={rootRef} className="relative">
      <div className="relative">
        <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={open && results[active] ? `${listId}-${active}` : undefined}
          value={shown}
          placeholder={selected ? selected.name : placeholder}
          onFocus={() => setOpen(true)}
          onClick={() => setOpen(true)}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onKeyDown={onKeyDown}
          autoComplete="off"
          className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 pl-10 pr-16 py-3 rounded-xl text-xs font-bold text-slate-800 dark:text-slate-100 placeholder:text-slate-400"
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {(selected || query) && (
            <button
              type="button"
              onClick={() => { onChange(''); setQuery(''); setOpen(true); inputRef.current?.focus(); }}
              className="p-1 rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
              aria-label="Limpar paciente"
            >
              <X size={14} />
            </button>
          )}
          <ChevronDown size={14} className="text-slate-400 pointer-events-none" />
        </div>
      </div>
      {/* Mantém a validação nativa de campo obrigatório do formulário. */}
      {required && (
        <input
          tabIndex={-1}
          aria-hidden
          required
          value={value}
          onChange={() => undefined}
          onFocus={() => inputRef.current?.focus()}
          className="absolute bottom-0 left-1/2 w-px h-px opacity-0 pointer-events-none"
        />
      )}
      {open && (
        <ul
          ref={listRef}
          id={listId}
          role="listbox"
          className="absolute z-50 mt-1 w-full max-h-64 overflow-y-auto custom-scrollbar bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl py-1"
        >
          {results.length === 0 ? (
            <li className="px-4 py-3 text-xs text-slate-400 italic">Nenhum paciente encontrado</li>
          ) : (
            results.map((p, i) => (
              <li
                key={p.id}
                id={`${listId}-${i}`}
                data-index={i}
                role="option"
                aria-selected={p.id === value}
                onMouseDown={(e) => { e.preventDefault(); choose(p); }}
                onMouseEnter={() => setActive(i)}
                className={`px-4 py-2.5 text-xs font-bold cursor-pointer ${
                  i === active ? 'bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300' : 'text-slate-700 dark:text-slate-200'
                } ${p.id === value ? 'underline decoration-indigo-400' : ''}`}
              >
                {p.name}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
};

export default PatientSearchSelect;
